import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell, ComposedChart, Scatter } from 'recharts';
import { DeliveryData } from './types';
import { getCpoPkColor, getProductColor, dashboardColors } from './colors';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  useColorModeValue,
  useBreakpointValue,
  Flex,
} from '@chakra-ui/react';
import { formatNumber, formatNumber2Digit } from './utils';
import type { ReactElement, ReactSVGElement } from 'react';

interface PengirimanCpoPkCkProps {
  data: DeliveryData[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.600');

  if (active && payload?.length) {
    const chartData = payload[0]?.payload;
    return (
      <Box bg={bg} p={3} borderWidth={1} borderColor={border} borderRadius="md" shadow="md">
        <Text fontWeight="semibold">{`${label}`}</Text>
        {payload.map((entry: any) => {
          // Ambil value true jika ada, jika tidak, gunakan logika 1=0
          let value = entry.value;
          if (entry.dataKey === 'daily' && typeof chartData.dailyTrue !== 'undefined') {
            value = chartData.dailyTrue;
          }
          if (entry.dataKey === 'mtd' && typeof chartData.mtdTrue !== 'undefined') {
            value = chartData.mtdTrue;
          }
          // Jika value hasil manipulasi (1), tampilkan 0
          if (value === 1 && ((entry.dataKey === 'daily' && chartData.dailyTrue === 0) || (entry.dataKey === 'mtd' && chartData.mtdTrue === 0))) {
            value = 0;
          }
          return (
            <Text key={`tooltip-${entry.name}`} fontSize="sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formatNumber(value)} kg`}
            </Text>
          );
        })}
      </Box>
    );
  }
  return null;
};

const renderCustomBarLabel = (props: any) => {
  const { x, y, width, value, dataKey, payload } = props;
  
  // Debug: log untuk melihat nilai sebenarnya
  console.log(`Label debug - dataKey: ${dataKey}, value: ${value}, payload:`, payload);
  
  // Ambil nilai asli dari payload
  let actualValue = value;
  if (dataKey === 'daily' && typeof payload.dailyTrue !== 'undefined') {
    actualValue = payload.dailyTrue;
  }
  if (dataKey === 'mtd' && typeof payload.mtdTrue !== 'undefined') {
    actualValue = payload.mtdTrue;
  }
  
  console.log(`Actual value for ${dataKey}:`, actualValue);
  
  // Jika data asli 0, jangan tampilkan label sama sekali
  if (actualValue === 0) {
    console.log(`Hiding label for ${dataKey} because actualValue is 0`);
    return null;
  }
  
  const safeY = y < 18 ? 18 : y - 6;
  
  return (
    <text
      x={x + width / 2}
      y={safeY}
      fill="#222"
      textAnchor="middle"
      fontSize={12}
      fontWeight="bold"
    >
      {formatNumber(actualValue)}
    </text>
  );
};

const PengirimanCpoPkCk: React.FC<PengirimanCpoPkCkProps> = ({ data }) => {

  const getProductBarColor = (product: string) => {
    if (product === 'CPO') {
      return dashboardColors.cpo;
    } else if (product === 'PK') {
      return dashboardColors.pk;
    }
    return dashboardColors.cpo; // default fallback
  };

  // Ukuran lingkaran dinamis berdasarkan breakpoint
  const dotSize = useBreakpointValue({ 
    base: 14,         // mobile - smaller
    md: 14,           // tablet
    lg: 14,          // laptop
    xl: 14,          // desktop
    '2xl': 18        // large desktop
  }) || 14;

  // Custom dot renderer for MTD scatter - DIKOMENTAR
  // const renderMtdDot = (props: any) => {
  //   const { cx, cy, payload } = props;
  //   const radius = dotSize;
    
  //   // Only show dot if MTD > 0
  //   if (!payload.mtd || payload.mtd <= 0) {
  //     return <g></g>;
  //   }
    
  //   return (
  //     <g>
  //       <circle 
  //         cx={cx} 
  //         cy={cy} 
  //         r={radius}
  //         fill={dashboardColors.error}
  //         stroke="#fff"
  //         strokeWidth="2"
  //       />
  //       <text 
  //         x={cx} 
  //         y={cy} 
  //         fill="#fff" 
  //         textAnchor="middle" 
  //         dominantBaseline="middle"
  //         fontSize={Math.max(8, radius - 6)}
  //         fontWeight="bold"
  //       >
  //         {payload.mtd ? `${formatNumber2Digit(payload.mtd/1000000)} T` : ''}
  //       </text>
  //     </g>
  //   );
  // };

  // Transform data for chart
  // const chartData = [
  //   {
  //     product: 'CPO',
  //     daily: data.find(d => d.uraian === 'Pengiriman CPO')?.daily || 0,
  //     mtd: data.find(d => d.uraian === 'Pengiriman CPO')?.mtd || 0
  //   },
  //   {
  //     product: 'PK',
  //     daily: data.find(d => d.uraian === 'Pengiriman PK')?.daily || 0,
  //     mtd: data.find(d => d.uraian === 'Pengiriman PK')?.mtd || 0
  //   },
  // ];
  const chartData = [
  {
    product: 'CPO',
    daily: Math.max(data.find(d => d.uraian === 'Pengiriman CPO')?.daily || 0, 1000),
    mtd: Math.max(data.find(d => d.uraian === 'Pengiriman CPO')?.mtd || 0, 1000),
    dailyTrue: data.find(d => d.uraian === 'Pengiriman CPO')?.daily || 0,
    mtdTrue: data.find(d => d.uraian === 'Pengiriman CPO')?.mtd || 0,
  },
  {
    product: 'PK',
    daily: Math.max(data.find(d => d.uraian === 'Pengiriman PK')?.daily || 0, 1000),
    mtd: Math.max(data.find(d => d.uraian === 'Pengiriman PK')?.mtd || 0, 1000),
    dailyTrue: data.find(d => d.uraian === 'Pengiriman PK')?.daily || 0,
    mtdTrue: data.find(d => d.uraian === 'Pengiriman PK')?.mtd || 0,
  },
];

  // Calculate totals
  const cpoData = data.find(d => d.uraian === 'Pengiriman CPO');
  const pkData = data.find(d => d.uraian === 'Pengiriman PK');
  const ckData = data.find(d => d.uraian === 'Pengiriman CK');

  const rowHeight = useBreakpointValue({ 
      base: '110%',
      md: '110%',
      lg: '100%',
      xl: '100%',
      '2xl': '120%'
  });

  const barSize = useBreakpointValue({ 
      base: 20,
      md: 25,
      lg: 30,
      xl: 30,
      '2xl': 35
  });

  const chartMargin = useBreakpointValue({ 
      base: { top: 10, right: 10, left: 10, bottom: 20 },
      md: { top: 15, right: 15, left: 15, bottom: 25 },
      lg: { top: 20, right: 30, left: 20, bottom: 5 },
      xl: { top: 20, right: 30, left: 20, bottom: 5 },
      '2xl': { top: 25, right: 35, left: 25, bottom: 10 }
  });

  const chartMinHeight = useBreakpointValue({
    base: '380px',
    md: '380px',
    lg: '320px',
    xl: '270px',
    '2xl': '300px'
  }) || '300px';

const renderDailyLabel = (props: any) => {
  const { x, y, width, value, payload } = props;

  // Ambil nilai asli dari payload
  const actualValue = payload?.dailyTrue ?? (value === 1000 ? 0 : value);

  // Jika data asli 0, render invisible label (empty text element)
  if (actualValue === 0) {
    return (
      <text
        x={x + width / 2}
        y={y}
        fill="none"
        textAnchor="middle"
        fontSize={9}
        fontWeight="bold"
      ></text>
    );
  }

  const safeY = y < 18 ? 18 : y - 6;

  return (
    <text
      x={x + width / 2}
      y={safeY}
      fill="#222"
      textAnchor="middle"
      fontSize={9}
      fontWeight="bold"
    >
      {formatNumber(actualValue)}
    </text>
  );
};

const renderMtdLabel = (props: any) => {
  const { x, y, width, value, payload } = props;

  // Ambil nilai asli dari payload
  const actualValue = payload?.mtdTrue ?? (value === 1000 ? 0 : value);

  // Jika data asli 0, render invisible label (empty text element)
  if (actualValue === 0) {
    return (
      <text
        x={x + width / 2}
        y={y}
        fill="none"
        textAnchor="middle"
        fontSize={9}
        fontWeight="bold"
      ></text>
    );
  }

  const safeY = y < 18 ? 18 : y - 6;

  return (
    <text
      x={x + width / 2}
      y={safeY}
      fill="#222"
      textAnchor="middle"
      fontSize={9}
      fontWeight="bold"
    >
      {formatNumber(actualValue)}
    </text>
  );
};

  return (
    <Box bg={useColorModeValue('white', 'gray.700')} borderRadius="lg" p={4} h="100%" display="flex" flexDirection="column">
      <Heading as="h3" size="md" mb={2} color={useColorModeValue('gray.800', 'white')} fontSize="18px">
        PENGIRIMAN CPO & PK
      </Heading>
      
      {/* Summary - YTD Values */}
      <Flex align="center" gap={6} mb={4} mt={0} justify="center">
        <Text fontSize="sm" color="#15803D" fontWeight="semibold">
          CPO YTD: <Box as="span" fontWeight="bold" color="#15803D" display="inline">{cpoData ? formatNumber(cpoData.ytd) : '0'}</Box>
        </Text>
        
        <Text fontSize="sm" color="#15803D" fontWeight="semibold">
          PK YTD: <Box as="span" fontWeight="bold" color="#15803D" display="inline">{pkData ? formatNumber(pkData.ytd) : '0'}</Box>
        </Text>
      </Flex>

      {/* Chart */}
      <Box minH={chartMinHeight} h={chartMinHeight} w="100%" display="flex" justifyContent="center">
        <ResponsiveContainer width="95%" height={rowHeight}>
          <ComposedChart
            data={chartData}
            margin={{ top: 0, right: 5, left: 20, bottom: 10 }}
            // barGap={10}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="product" 
              fontSize={12}
              height={60}
            />
            {/* Y Axis dengan logarithmic scale */}
            <YAxis 
              fontSize={12}
              scale="log"
              domain={[1000, 'dataMax']}
              tickFormatter={formatNumber}
              label={{
                value: 'Berat (kg)',
                angle: -90,
                position: 'insideLeft',
                offset: -15,
                fontSize: 12,
                fill: '#666',
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="bottom"
              align="center"
              height={30}
              wrapperStyle={{ fontSize: 12, bottom: 45 }}
            />
            
            {/* Bar untuk Daily */}
            <Bar 
              dataKey="daily" 
              name="Daily"
              // barSize={barSize}
              fill={dashboardColors.info} // biru
              label={renderDailyLabel}
            />
            
            {/* Bar untuk MTD */}
            <Bar 
              dataKey="mtd" 
              name="MTD"
              // barSize={barSize}
              fill={dashboardColors.warning} // hijau
              label={renderMtdLabel}
            />
            
            {/* Scatter untuk MTD dots */}
            {/* <Scatter
              yAxisId="mtd"
              dataKey="mtd"
              fill={dashboardColors.error}
              name="MTD"
              shape={renderMtdDot}
            /> */}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default PengirimanCpoPkCk;